import { useEffect, useState, useCallback } from 'react';

const STORAGE_KEY = 'app.themeMode';

function applyClass(mode) {
  // Tailwind is configured with `darkMode: 'class'` — flipping the
  // `dark` class on <html> activates every `dark:` utility in the tree.
  const root = document.documentElement;
  if (mode === 'dark') {
    root.classList.add('dark');
    root.classList.remove('light');
  } else {
    root.classList.add('light');
    root.classList.remove('dark');
  }
}

function readInitialMode() {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'dark' || stored === 'light') return stored;
  } catch (err) {
    // Private-mode / blocked storage — fall through to OS preference.
    console.debug('useThemeMode: localStorage read blocked', err);
  }
  // Fall back to the user's OS-level preference on first load.
  if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }
  return 'light';
}

/**
 * Module-level theme-mode state. Exposing this through a tiny hook
 * (rather than a Context provider) keeps the surgery small — any
 * component that needs the toggle just imports the hook.
 */
const _subs = new Set();
let _mode = typeof window !== 'undefined' ? readInitialMode() : 'light';
if (typeof window !== 'undefined') applyClass(_mode);

function _set(newMode) {
  _mode = newMode;
  applyClass(newMode);
  try { window.localStorage.setItem(STORAGE_KEY, newMode); }
  catch (err) {
    // Private-mode / quota exceeded — the in-memory state still flips,
    // user just loses persistence across reloads.
    console.debug('useThemeMode: localStorage write blocked', err);
  }
  for (const fn of _subs) fn(newMode);
}

export function useThemeMode() {
  const [mode, setMode] = useState(_mode);

  useEffect(() => {
    const sub = (next) => setMode(next);
    _subs.add(sub);
    return () => _subs.delete(sub);
  }, []);

  const toggle = useCallback(() => {
    // Read from `mode` (component state) so the dep array is honest about
    // what this callback closes over. `mode` always equals `_mode` because
    // the subscribe effect above keeps them in sync.
    _set(mode === 'dark' ? 'light' : 'dark');
  }, [mode]);

  const setLight = useCallback(() => _set('light'), []);
  const setDark = useCallback(() => _set('dark'), []);

  return { mode, isDark: mode === 'dark', toggle, setLight, setDark };
}

export default useThemeMode;
